import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { CreateVirtualCardScreen } from 'src/app/CreateVirtualCardForm/CreateVirtualCardScreen'
import ActionRightFlyoutContent from 'src/components/ActionRightFlyoutContent/ActionRightFlyoutContent'
import {
  CreditCardDropdownInfiniteScrollProps,
  CreditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import { CustomVirtualCardAddressFlatFields } from 'src/components/CustomVirtualCardAddressBox/CustomVirtualCardAddressBox'
import { PhysicalVirtualCardAddressFlatFields } from 'src/components/PhysicalVirtualCardBox/PhysicalVirtualCardBox'
import RecipientsTooltip from 'src/components/RecipientsTooltip/RecipientsTooltip'
import { RecurrenceFormValues } from 'src/components/RecurrenceForm/RecurrenceForm'
import {
  UserDropdownInfiniteScrollProps,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import VirtualCardForm, {
  VirtualCardFlatFormValues,
} from 'src/components/VirtualCardForm/VirtualCardForm'
import { ImageNames, OrganizationRoles, VirtualCardTypes } from 'src/enums'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns from 'src/localization/Trns'
import { ReceiptAttachmentModel } from 'src/models/receiptAttachment.models'
import {
  RecurrencePeriod,
  RecurrenceTerminator,
} from 'src/models/recurrence.models'
import { UserAssociation } from 'src/models/user.models'
import { useQueryOrganizationFeatureFlags } from 'src/queries/organizations/featureflags.queries'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectCreditCardListModelsFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectQueryParams } from 'src/redux/selectors/router.selectors'
import { PostBulkVirtualCardRequest } from 'src/requests/postBulkVirtualCard.request'
import { PostReceiptAttachmentRequest } from 'src/requests/postReceiptAttachment.request'
import { PostVirtualCardRequest } from 'src/requests/postVirtualCard.request'
import { toCents } from 'src/utils/currency.utils'
import { ErrorDetailMap } from 'src/utils/error.utils'
import { css } from 'styled-components'

interface CreateVirtualCardRecurringContentProps {
  userOptions: UserOption[]
  recentUserOptions?: UserOption[]
  isFormDisabled: boolean
  labelCreditCardsAsAccounts: boolean
  creditCardOptions: CreditCardOption[]
  receiptAttachments: ReceiptAttachmentModel[]
  uploadingAttachmentThumbnails: Record<string, string>
  postingVirtualCard: boolean
  topLevelError?: string
  errorDetailMap?: ErrorDetailMap<string>
  uploadAttachments: (attachments: PostReceiptAttachmentRequest[]) => void
  deleteAttachment: (attachmentId: string) => void
  postVirtualCardAndOpenToastAndCloseFlyout: (
    body: Partial<PostVirtualCardRequest>,
    inviteUsers?: boolean,
    role?: OrganizationRoles,
  ) => void
  postBulkVirtualCardAndOpenToastAndCloseFlyout: (
    body: Partial<PostBulkVirtualCardRequest>,
    inviteUsers?: boolean,
    role?: OrganizationRoles,
  ) => void
  setScreen: (val: CreateVirtualCardScreen) => void
  userInfiniteScrollProps: UserDropdownInfiniteScrollProps
  creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps
  pageLocation: string
  guestCard?: boolean
  showSoftAlert?: boolean
}

const CreateVirtualCardRecurringContent: React.FC<
  CreateVirtualCardRecurringContentProps
> = (props) => {
  const queryParams = useSelector(selectQueryParams)
  const { analytics } = useAnalyticsContext()
  const user = useSelector(selectUserListModelFromState)
  const creditCards = useSelector(selectCreditCardListModelsFromState)
  const guestCardImage = useColorOrGrayscaleImage(ImageNames.CardTypeGuest)
  const { data: orgFeatureFlags } = useQueryOrganizationFeatureFlags({
    orgId: user?.organizationId,
  })
  const showGuestRole = orgFeatureFlags?.guestRoleEnabled

  const onSuccessfulSubmit = (
    flatFields: VirtualCardFlatFormValues,
    expenseDetails: Record<string, string>,
    recurrenceFields: RecurrenceFormValues,
    customAddressFields: CustomVirtualCardAddressFlatFields,
    physicalAddressFields: PhysicalVirtualCardAddressFlatFields,
    role?: OrganizationRoles,
  ): void => {
    const fields = {
      creditCardId: flatFields.creditCardId,
      displayName: flatFields.displayName,
      expenseDetails: Object.entries(expenseDetails)
        .filter((keyVal) => !!keyVal[1])
        .map(([key, val]) => ({ categoryId: key, labelId: val })),
      balanceCents:
        flatFields.balanceDollars !== undefined
          ? toCents(flatFields.balanceDollars)
          : 0,
      currency: flatFields.currency,
      notes: flatFields.notes,
      receiptAttachmentIds: props.receiptAttachments.map((att) => att.id),
      recurs: true,
      recurrence: {
        // TODO: maybe don't submit 0 here?
        balanceCents:
          flatFields.balanceDollars !== undefined
            ? toCents(flatFields.balanceDollars)
            : 0,
        period: recurrenceFields.period,
        interval: recurrenceFields.interval,
        terminator: recurrenceFields.terminator,
        count:
          recurrenceFields.terminator === RecurrenceTerminator.Count
            ? recurrenceFields.count
            : undefined,
        until:
          recurrenceFields.terminator === RecurrenceTerminator.Date
            ? recurrenceFields.untilMoment?.format('YYYY-MM-DD')
            : undefined,
        byWeekDay:
          recurrenceFields.period === RecurrencePeriod.Weekly
            ? recurrenceFields.byWeekDay
            : undefined,
        byMonthDay:
          recurrenceFields.period === RecurrencePeriod.Monthly
            ? recurrenceFields.byMonthDay
            : undefined,
        byYearDay:
          recurrenceFields.period === RecurrencePeriod.Yearly
            ? recurrenceFields.byYearDay
            : undefined,
      },
      direct: props.guestCard,
      address1: flatFields.customBillingAddress
        ? customAddressFields.address1
        : undefined,
      address2: flatFields.customBillingAddress
        ? customAddressFields.address2
        : undefined,
      city: flatFields.customBillingAddress
        ? customAddressFields.city
        : undefined,
      province: flatFields.customBillingAddress
        ? customAddressFields.province
        : undefined,
      postal: flatFields.customBillingAddress
        ? customAddressFields.postal
        : undefined,
      country: flatFields.customBillingAddress
        ? customAddressFields.country
        : undefined,
      hasPlasticCard: flatFields.includePhysicalCard,
      plasticFirstName: flatFields.includePhysicalCard
        ? flatFields.plasticFirstName
        : undefined,
      plasticLastName: flatFields.includePhysicalCard
        ? flatFields.plasticLastName
        : undefined,
      plasticAddress1:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.address1
          : undefined,
      plasticAddress2:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.address2
          : undefined,
      plasticCity:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.city
          : undefined,
      plasticProvince:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.province
          : undefined,
      plasticPostal:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.postal
          : undefined,
      plasticCountry:
        flatFields.includePhysicalCard &&
        flatFields.physicalCustomShippingAddress
          ? physicalAddressFields.country
          : undefined,
    }

    flatFields.userEmails?.length === 1
      ? props.postVirtualCardAndOpenToastAndCloseFlyout(
          {
            ...fields,
            recipient: flatFields.userEmails[0],
            receiptRulesExempt: flatFields.receiptRulesExempt,
          },
          !props.guestCard,
          role,
        )
      : props.postBulkVirtualCardAndOpenToastAndCloseFlyout(
          {
            ...fields,
            recipients: flatFields.userEmails,
          },
          !props.guestCard,
          role,
        )

    analytics?.track('Sucessfully create virtual card', {
      type_of_card: props.guestCard
        ? VirtualCardTypes.recurringGuest
        : VirtualCardTypes.recurring,
      card_name: flatFields.displayName,
      credit_limit: flatFields.balanceDollars,
      active_until: flatFields.validToMoment?.format('YYYY-MM-DD'),
      expense_category: Object.keys(expenseDetails).length > 0,
      add_attachments:
        props.receiptAttachments && props.receiptAttachments.length > 0,
      add_notes: !!flatFields.notes,
      recipient_count: flatFields.userEmails?.length,
      is_recipient_me:
        flatFields.userEmails &&
        user &&
        flatFields.userEmails.includes(user.email),
      receipt_rules_enabled: !!flatFields.receiptRulesExempt,
      number_recurrences:
        recurrenceFields.count && recurrenceFields.count > 0
          ? recurrenceFields.count
          : undefined,
      recurrence_period: recurrenceFields.period,
      issuer:
        creditCards &&
        flatFields.creditCardId &&
        creditCards[flatFields.creditCardId]
          ? creditCards[flatFields.creditCardId].issuer
          : undefined,
    })
  }

  const initialFlatFields = {
    // TODO: refactor all uses of queryParams to handle arrays and normalize to string[]
    creditCardId:
      typeof queryParams.creditCardId === 'string'
        ? queryParams.creditCardId
        : undefined,
  }

  return (
    <ActionRightFlyoutContent
      title={<Trns source={'Create a Virtual Card'} />}
      specificElements={{
        subtitle: props.guestCard ? (
          <Trns source={'Recurring Guest'} />
        ) : (
          <Trns source={'Recurring'} />
        ),
        imageSrc: guestCardImage,
        changeLink: {
          ariaLabelSource: 'Change Card Type',
          changeMessage: <Trns source={'Change Card Type'} />,
          onChangeClick: (): void => {
            props.setScreen(CreateVirtualCardScreen.Pick)
          },
        },
      }}
    >
      <VirtualCardForm
        id={'create'}
        isRecurringPayment
        buttonText={<Trns source={'Create a Virtual Card'} />}
        multiCardButtonText={<Trns source={'Create Virtual Cards'} />}
        onSuccessfulSubmit={onSuccessfulSubmit}
        loading={props.postingVirtualCard}
        userOptions={props.userOptions}
        recentUserOptions={props.recentUserOptions}
        userFieldLabel={<Trns source={'Recipients'} />}
        customAddressEditable
        physicalCardEditable
        recipientNamesEditable={false}
        recurrenceEditable
        notesEditable
        creditCardEditable
        amountProps={{
          amountEditable: true,
        }}
        initialFlatFields={initialFlatFields}
        creditCardProps={{
          isFormDisabled: props.isFormDisabled,
          creditCardOptions: props.creditCardOptions,
          labelCreditCardsAsAccounts: props.labelCreditCardsAsAccounts,
        }}
        attachmentsProps={{
          receiptAttachments: props.receiptAttachments,
          uploadingAttachmentThumbnails: props.uploadingAttachmentThumbnails,
          uploadAttachments: props.uploadAttachments,
          deleteAttachment: props.deleteAttachment,
        }}
        topLevelError={props.topLevelError}
        errorDetailMap={props.errorDetailMap}
        userInfiniteScrollProps={props.userInfiniteScrollProps}
        creditCardInfiniteScrollProps={props.creditCardInfiniteScrollProps}
        tooltip={
          <RecipientsTooltip
            openLeft={false}
            css={css`
              z-index: 1;
              padding-bottom: 6px;
            `}
          />
        }
        isMulti
        suggestedProps={{
          showSuggested: true,
          showOwnUserInSuggested: true,
        }}
        filterUserbyAssocationTypes={[UserAssociation.virtualCardTo]}
        unableToAddNewUser={
          showGuestRole
            ? false
            : !user?.hasOrgAdminPermissions && !props.guestCard
        }
        typeofCard={
          props.guestCard
            ? VirtualCardTypes.recurringGuest
            : VirtualCardTypes.recurring
        }
        showCardApprovalSoftAlert={props.showSoftAlert}
      />
    </ActionRightFlyoutContent>
  )
}

export default CreateVirtualCardRecurringContent
